'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import FadeIn from '../ui/FadeIn';
import MagneticButton from '../ui/MagneticButton';
import { useTranslations } from '@/lib/i18n/TranslationProvider';
import { Play } from 'lucide-react';

const CHROME_URL = "https://chromewebstore.google.com/detail/wa-contacts-extractor-wav/dembjmabcapfkbnbbcphbfcjoghlkcoi";

const CYCLE_MS = 6000;

const screenImages = [
    { src: "/screenshots/insight.png", alt: "WaVault Dashboard — WhatsApp community analytics" },
    { src: "/screenshots/group.png", alt: "WaVault Groups — export WhatsApp community groups" },
    { src: "/screenshots/country.png", alt: "WaVault Country — sort contacts by 200+ countries" },
    { src: "/screenshots/new-chat.png", alt: "WaVault New Chat — instant WhatsApp messaging" },
];

export default function Hero() {
    const { t } = useTranslations();
    const screens = screenImages.map((img, i) => ({
        ...img,
        label: t.hero.screens[i].label,
        heading: t.hero.screens[i].heading,
        desc: t.hero.screens[i].desc,
    }));

    const [active, setActive] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const next = useCallback(() => {
        setActive((prev) => (prev + 1) % screens.length);
    }, [screens.length]);

    useEffect(() => {
        if (isPaused) return;
        const timer = setInterval(next, CYCLE_MS);
        return () => clearInterval(timer);
    }, [isPaused, next]);

    return (
        <section className="relative overflow-hidden bg-[var(--color-md-surface)]">

            {/* ── Ambient background: More subtle for Kinetic feel ── */}
            <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden="true">
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-md-primary-container)]/10 via-transparent to-transparent" />
                <div className="absolute top-[-5%] right-[5%] w-[800px] h-[800px] bg-[var(--color-md-primary)]/[0.03] rounded-full blur-[150px]" />
            </div>

            <div className="container mx-auto px-6 relative z-10 pt-28 pb-12 min-h-[max(80vh,700px)] flex items-center">
                <div className="grid items-center gap-10 lg:gap-16 lg:grid-cols-[1.1fr_0.9fr] w-full max-w-7xl mx-auto">

                    {/* ── LEFT: Typography & Conversion ─────────── */}
                    <div className="max-w-2xl pt-2 text-center lg:text-left mx-auto lg:mx-0 flex flex-col justify-center">

                        {/* Premium Badge: Minimalist & Clean */}
                        <FadeIn priority={true} delay={0.05}>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-md-surface-variant)]/50 border border-[var(--color-md-outline-variant)] text-[10px] uppercase font-bold tracking-[0.15em] text-[var(--color-md-primary)] mb-6 transition-all hover:bg-[var(--color-md-surface-variant)] cursor-default">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--color-md-primary)] opacity-40"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--color-md-primary)]"></span>
                                </span>
                                {t.hero.badge}
                            </div>
                        </FadeIn>

                        {/* Headline: Editorial Scale but better spaced */}
                        <FadeIn priority={true} delay={0.1}>
                            <h1 className="text-[clamp(2.75rem,5.5vw,4.25rem)] font-black tracking-[-0.05em] leading-[1.1] text-[var(--color-md-on-surface)] mb-6">
                                {t.hero.headline} <span className="text-brand-gradient block text-glow-primary mt-1 pb-2">{t.hero.headlineAccent}</span>
                            </h1>
                            <p className="text-lg md:text-xl text-[var(--color-md-on-surface-variant)] font-medium mb-10 max-w-xl leading-relaxed opacity-90">
                                {t.hero.subtitle}
                            </p>
                        </FadeIn>

                        {/* Primary CTA Area: Enhanced Physics */}
                        <FadeIn priority={true} delay={0.15} yOffset={10}>
                            <div className="flex flex-col items-center lg:items-start gap-6 mb-12">
                                <div className="magnetic-shift shrink-0">
                                    <MagneticButton>
                                        <Button
                                            size="lg"
                                            className="px-14 h-16 text-lg font-black btn-glow rounded-full shadow-cinematic-3 hover:shadow-cinematic-4"
                                            href={CHROME_URL}
                                            target="_blank"
                                        >
                                            {t.hero.cta}
                                        </Button>
                                    </MagneticButton>
                                </div>
                                
                                {/* Social Proof Row: Better breathing space */}
                                <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-[10px] font-black text-[var(--color-md-on-surface-variant)] tracking-[0.15em] uppercase opacity-70 justify-center lg:justify-start">
                                    <div className="flex items-center gap-2">
                                        <div className="flex gap-0.5" aria-hidden="true">
                                            {[1,2,3,4,5].map(i => <span key={i} className="text-amber-500 text-lg leading-none">★</span>)}
                                        </div>
                                        <span className="ml-1 text-[var(--color-md-on-surface)]" aria-label={`Rating: ${t.hero.rating}`}>{t.hero.rating}</span>
                                    </div>
                                    <span className="w-1 h-1 rounded-full bg-[var(--color-md-outline)] hidden sm:inline-block" />
                                    <span>{t.hero.noSignup}</span>
                                    <span className="w-1 h-1 rounded-full bg-[var(--color-md-outline)] hidden sm:inline-block" />
                                    <span>{t.hero.local}</span>
                                    <span className="w-1 h-1 rounded-full bg-[var(--color-md-outline)] hidden sm:inline-block" />
                                    <span className="text-[#10b981] font-extrabold">Actively Maintained for WhatsApp Web</span>
                                </div>
                            </div>
                        </FadeIn>

                        {/* ── Feature Navigation ── */}
                        <FadeIn delay={0.25} yOffset={10}>
                            <div className="relative border-t border-[var(--color-md-outline)]/20 pt-6"
                                onMouseEnter={() => setIsPaused(true)}
                                onMouseLeave={() => setIsPaused(false)}
                            >
                                {/* Tab line indicator: Pixel perfect */}
                                <div className="absolute top-[-1px] left-0 h-[2px] w-full bg-transparent flex">
                                    {screens.map((_, i) => (
                                        <div key={i} className="flex-1 px-1">
                                            <div className="h-full rounded-full bg-[var(--color-md-outline)]/20 overflow-hidden">
                                                {i === active && !isPaused && (
                                                    <motion.div
                                                        className="h-full bg-[var(--color-md-primary)] shadow-[0_0_8px_var(--color-md-primary)]"
                                                        initial={{ width: "0%" }}
                                                        animate={{ width: "100%" }}
                                                        transition={{ duration: CYCLE_MS / 1000, ease: "linear" }}
                                                    />
                                                )}
                                                {i === active && isPaused && (
                                                    <div className="h-full bg-[var(--color-md-primary)] w-full shadow-[0_0_8px_var(--color-md-primary)]" />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
                                    {screens.map((s, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActive(i)}
                                            className={`text-left text-[10px] font-black uppercase tracking-widest transition-all duration-300 py-3 ${i === active
                                                ? 'text-[var(--color-md-on-surface)]'
                                                : 'text-[var(--color-md-on-surface-variant)] opacity-50 hover:opacity-100'
                                                }`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Active Feature Description: Refined luminous accent */}
                                <div className="min-h-[140px] md:min-h-[120px]">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={active}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            className="px-8 py-6 rounded-2xl bg-[var(--color-md-surface-variant)]/40 luminous-accent-line border border-[var(--color-md-outline)]/5 transition-colors"
                                        >
                                            <motion.h3 
                                                variants={{
                                                    hidden: { opacity: 0, x: -10 },
                                                    visible: { opacity: 1, x: 0 }
                                                }}
                                                className="text-lg font-black mb-2 uppercase tracking-wider text-[var(--color-md-on-surface)]"
                                            >
                                                {screens[active].heading}
                                            </motion.h3>
                                            <div className="flex flex-wrap gap-x-1.5">
                                                {screens[active].desc.split(" ").map((word, i) => (
                                                    <motion.span
                                                        key={i}
                                                        variants={{
                                                            hidden: { opacity: 0, y: 5, filter: "blur(4px)" },
                                                            visible: { 
                                                                opacity: 1, 
                                                                y: 0, 
                                                                filter: "blur(0px)",
                                                                transition: { 
                                                                    delay: i * 0.03,
                                                                    duration: 0.4,
                                                                    ease: [0.2, 0.65, 0.3, 0.9]
                                                                }
                                                            }
                                                        }}
                                                        className="text-[var(--color-md-on-surface-variant)] leading-relaxed text-sm font-bold opacity-95"
                                                    >
                                                        {word}
                                                    </motion.span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </div>
                        </FadeIn>
                    </div>

                    {/* ── RIGHT: Product Showcase (Above-the-Fold Optimization) ─────────── */}
                    <FadeIn delay={0.3} yOffset={40} className="relative z-10 w-full flex justify-center lg:justify-end h-full items-center lg:pl-10">
                        <div
                            className="relative group w-full flex justify-center perspective-1000 max-w-[440px] lg:max-w-none"
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            {/* Refined Frosted-Glass Showcase Frame */}
                            <div className="relative z-10 p-2 md:p-3 rounded-[32px] frosted-glass shadow-cinematic-4 transform-gpu rotate-y-[-10deg] rotate-x-[5deg] group-hover:rotate-y-[0deg] group-hover:rotate-x-[0deg] transition-all duration-1000 ease-out">
                                <div className="relative rounded-[26px] overflow-hidden bg-[var(--md-sys-color-surface-dim)] border border-[var(--color-md-outline)]/10 shadow-product">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={active}
                                            initial={{ opacity: 0, filter: "blur(10px)", scale: 1.05 }}
                                            animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
                                            exit={{ opacity: 0, filter: "blur(10px)", scale: 0.95 }}
                                            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                                            className="relative flex items-center justify-center bg-[#0D1117]"
                                        >
                                            <Image
                                                src={screens[active].src}
                                                alt={screens[active].alt}
                                                width={724}
                                                height={1914}
                                                priority={active === 0}
                                                className="w-auto h-auto max-w-full max-h-[50vh] md:max-h-[60vh] lg:max-h-[72vh] object-contain"
                                                quality={100}
                                            />
                                        </motion.div>
                                    </AnimatePresence>
                                    {/* Subtle reflection overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/10 pointer-events-none" />
                                </div>
                            </div>

                            {/* Background Ambient Depth */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[var(--color-md-primary)]/[0.02] rounded-full blur-[120px] pointer-events-none" />
                        </div>
                    </FadeIn>
                </div>
            </div>

        </section>
    );
}
